import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import axiosInstance from '../../api/axios';
import Swal from 'sweetalert2';

/* ─── HELPERS & UTILS ─────────────────────────────────────── */
const fmtRupiah = (n) =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(n ?? 0);

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '-';

const STATUS_LABEL = {
  pending_billing: 'Menunggu Input Tagihan',
  billed:          'Tagihan Siap',
  draft:           'Menunggu Konfirmasi',
  paid:            'Lunas',
  cancelled:       'Dibatalkan',
};

const STATUS_COLOR = {
  pending_billing: '#0369a1',
  billed:          '#b45309',
  draft:           '#854d0e',
  paid:            '#166534',
  cancelled:       '#991b1b',
};

const STATUS_BG = {
  pending_billing: '#f0f9ff',
  billed:          '#fef3c7',
  draft:           '#fef9c3',
  paid:            '#dcfce7',
  cancelled:       '#fee2e2',
};

/* ─── StatusPill Component ──────────────────────────────────── */
function StatusPill({ status }) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '11px',
      fontWeight: '600',
      background: STATUS_BG[status] ?? '#f1f5f9',
      color: STATUS_COLOR[status] ?? '#475569',
      whiteSpace: 'nowrap',
    }}>
      <span style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: STATUS_COLOR[status] ?? '#94a3b8',
        display: 'inline-block',
        flexShrink: 0,
      }} />
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

/* ─── MODAL INPUT TAGIHAN ──────────────────────────────────── */
function ModalInputTagihan({ item, onClose, onSuccess }) {
  const [tarifList, setTarifList]       = useState([]);
  const [tarifLoading, setTarifLoading] = useState(true);
  const [items, setItems]               = useState([]);
  const [tarifSelect, setTarifSelect]   = useState('');
  const [catatanKasir, setCatatanKasir] = useState('');
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');

  useEffect(() => {
    const loadTarif = async () => {
      setTarifLoading(true);
      try {
        const res = await axiosInstance.get('/admin/tarif-tindakan', {
          params: { per_page: 100, hanya_aktif: true },
        });
        const raw = res.data?.data ?? res.data ?? [];
        const list = Array.isArray(raw) ? raw : [];
        setTarifList(list.sort((a, b) => Number(a.id) - Number(b.id)));
      } catch (err) {
        console.error(err);
        setTarifList([]);
      } finally {
        setTarifLoading(false);
      }
    };
    loadTarif();
  }, []);

  const tindakanDokter =
    item.rekam_medis?.tindakan ??
    item.pendaftaran?.rekam_medis?.tindakan ??
    item.pendaftaran?.rekamMedis?.tindakan ??
    '';

  const isTarifSelected = (id) => items.some((it) => it.tarif_tindakan_id === id);

  const toggleTarif = (tarif) => {
    if (isTarifSelected(tarif.id)) {
      setItems((p) => p.filter((it) => it.tarif_tindakan_id !== tarif.id));
    } else {
      setItems((p) => [
        ...p,
        {
          tarif_tindakan_id: tarif.id,
          nama_snapshot: tarif.nama,
          harga_satuan: String(Math.trunc(Number(tarif.harga) || 0)),
          qty: 1,
          jenis: 'tindakan',
        },
      ]);
    }
  };

  const onSelectTarif = (e) => {
    const id = e.target.value;
    if (!id) {
      setTarifSelect('');
      return;
    }
    const tarif = tarifList.find((t) => String(t.id) === id);
    if (tarif && !isTarifSelected(tarif.id)) toggleTarif(tarif);
    setTarifSelect('');
  };

  const subtotal = items.reduce(
    (s, it) => s + (parseFloat(it.harga_satuan) || 0) * (parseInt(it.qty) || 0),
    0
  );

  const handleSubmit = async () => {
    if (items.length === 0) {
      setError('Pilih minimal satu tindakan medis.');
      return;
    }
    for (const it of items) {
      if (!it.nama_snapshot.trim()) {
        setError('Nama item tidak boleh kosong.');
        return;
      }
      if (it.harga_satuan === '' || parseFloat(it.harga_satuan) < 0) {
        setError('Harga satuan tidak valid.');
        return;
      }
      if (parseInt(it.qty) < 1) {
        setError('Qty minimal 1.');
        return;
      }
    }
    setLoading(true);
    setError('');
    try {
      await axiosInstance.post(`/admin/transaksi/${item.id}/input-tagihan`, {
        items: items.map((it) => ({
          nama_snapshot: it.nama_snapshot.trim(),
          harga_satuan: parseFloat(it.harga_satuan),
          qty: parseInt(it.qty),
          jenis: it.jenis,
        })),
        catatan_kasir: catatanKasir || undefined,
      });
      onSuccess();
      Swal.fire({
        icon: 'success',
        title: 'Tagihan Disimpan',
        text: 'Rincian biaya tagihan berhasil disimpan.',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      setError(err.response?.data?.message ?? 'Gagal menyimpan tagihan.');
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#ffffff',
          borderRadius: 16,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          width: '100%',
          maxWidth: 600,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div style={{ height: 4, background: 'linear-gradient(90deg, #2563eb, #60a5fa)', borderRadius: '16px 16px 0 0' }} />
        <div style={{ padding: '24px' }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '.05em', margin: '0 0 4px' }}>
            Input Tagihan
          </p>
          <h3 style={{ fontSize: 18, fontWeight: 800, color: '#111827', margin: '0 0 16px' }}>
            Rincian Biaya Kunjungan
          </h3>

          <div style={{
            backgroundColor: '#F9FAFB',
            borderRadius: 10,
            border: '1px solid #E5E7EB',
            padding: '12px 16px',
            marginBottom: 16,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px 16px',
            fontSize: 13,
          }}>
            {[
              ['Invoice', item.nomor_invoice ?? '-'],
              ['Pasien', item.pendaftaran?.pasien?.nama ?? '-'],
              ['Dokter', item.pendaftaran?.dokter?.nama ?? '-'],
              ['Tanggal', fmtDate(item.created_at)],
            ].map(([k, v]) => (
              <div key={k}>
                <div style={{ fontSize: 11, color: '#6B7280' }}>{k}</div>
                <div style={{ fontWeight: 600, color: '#111827', marginTop: 1 }}>{v}</div>
              </div>
            ))}
          </div>

          {tindakanDokter && (
            <div style={{
              backgroundColor: '#EFF6FF',
              border: '1px solid #BFDBFE',
              borderRadius: 10,
              padding: '10px 14px',
              marginBottom: 16,
              fontSize: 13,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#1D4ED8', marginBottom: 4 }}>
                Tindakan Catatan Dokter:
              </div>
              <div style={{ color: '#1E40AF', lineHeight: 1.5 }}>{tindakanDokter}</div>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Pilih Tindakan Medis</label>
            {tarifLoading ? (
              <p style={{ fontSize: 13, color: '#6B7280', margin: 0 }}>Memuat daftar tarif…</p>
            ) : (
              <select style={inputStyle} value={tarifSelect} onChange={onSelectTarif}>
                <option value="">— Pilih tindakan dari master tarif —</option>
                {tarifList
                  .filter((t) => !isTarifSelected(t.id))
                  .map((tarif) => (
                    <option key={tarif.id} value={tarif.id}>
                      {tarif.nama} — {fmtRupiah(Math.trunc(Number(tarif.harga) || 0))}
                    </option>
                  ))}
              </select>
            )}
          </div>

          {items.length > 0 && (
            <div style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '12px', marginBottom: 16, fontSize: 13 }}>
              <div style={{ fontWeight: 700, color: '#166534', marginBottom: 8 }}>Item Tagihan Terpilih</div>
              {items.map((it, idx) => {
                const itemSubtotal = (parseFloat(it.harga_satuan) || 0) * (parseInt(it.qty) || 0);
                const tarif = tarifList.find((t) => t.id === it.tarif_tindakan_id);
                return (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: '600', color: '#111827', flex: '1 1 140px' }}>{it.nama_snapshot}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="text"
                        value={it.harga_satuan === '' ? '' : Number(it.harga_satuan).toLocaleString('id-ID')}
                        onChange={(e) => {
                          const valStr = e.target.value.replace(/\D/g, '');
                          setItems((p) => p.map((x, i) => i === idx ? { ...x, harga_satuan: valStr } : x));
                        }}
                        style={{ ...inputStyle, width: 90, padding: '4px 8px', fontSize: 12 }}
                        title="Harga Satuan"
                      />
                      <span style={{ color: '#9CA3AF' }}>×</span>
                      <input
                        type="number"
                        min="1"
                        value={it.qty}
                        onChange={(e) => {
                          const val = e.target.value;
                          setItems((p) => p.map((x, i) => i === idx ? { ...x, qty: val } : x));
                        }}
                        style={{ ...inputStyle, width: 50, padding: '4px 8px', fontSize: 12 }}
                        title="Jumlah"
                      />
                    </div>
                    <span style={{ fontWeight: 700, color: '#059669', minWidth: 90, textAlign: 'right' }}>
                      {fmtRupiah(itemSubtotal)}
                    </span>
                    <button
                      type="button"
                      onClick={() => tarif ? toggleTarif(tarif) : setItems((p) => p.filter((_, i) => i !== idx))}
                      style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 16, fontWeight: '700' }}
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Catatan Kasir (Opsional)</label>
            <input
              style={inputStyle}
              placeholder="Catatan tambahan untuk pembayaran..."
              value={catatanKasir}
              onChange={(e) => setCatatanKasir(e.target.value)}
            />
          </div>

          <div style={{
            backgroundColor: '#111827',
            borderRadius: 10,
            padding: '14px 18px',
            marginBottom: 18,
            display: 'flex',
            justify: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ fontSize: 13, color: '#9CA3AF', fontWeight: '500' }}>Total Tagihan</span>
            <span style={{ fontSize: 20, fontWeight: '800', color: '#34D399' }}>{fmtRupiah(subtotal)}</span>
          </div>

          {error && <p style={{ color: '#DC2626', fontSize: 13, margin: '0 0 14px', fontWeight: 500 }}>{error}</p>}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={btnSecondaryStyle}>Batal</button>
            <button type="button" onClick={handleSubmit} disabled={loading || subtotal <= 0} style={btnPrimaryStyle}>
              {loading ? 'Menyimpan…' : '✓ Simpan Tagihan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── MODAL KONFIRMASI LUNAS ───────────────────────────────── */
function ModalKonfirmasiLunas({ item, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleKonfirmasi = async () => {
    setLoading(true);
    setError('');
    try {
      await axiosInstance.post(`/admin/transaksi/${item.id}/bayar`, { metode_bayar: 'tunai' });
      onSuccess();
      Swal.fire({
        icon: 'success',
        title: 'Pembayaran Lunas!',
        text: 'Transaksi telah berhasil ditandai sebagai lunas.',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      setError(err.response?.data?.message ?? 'Gagal mengkonfirmasi pembayaran.');
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#ffffff', borderRadius: 16, width: '100%', maxWidth: 420, overflow: 'hidden' }}>
        <div style={{ height: 4, background: 'linear-gradient(90deg, #10B981, #34D399)' }} />
        <div style={{ padding: '24px' }}>
          <h3 style={{ fontSize: 17, fontWeight: 800, color: '#111827', margin: '0 0 8px' }}>Konfirmasi Pembayaran Lunas?</h3>
          <p style={{ fontSize: 13, color: '#4B5563', margin: '0 0 16px', lineHeight: 1.5 }}>
            Transaksi invoice <strong style={{ color: '#111827' }}>{item.nomor_invoice}</strong> akan ditandai sebagai LUNAS dengan metode Tunai.
          </p>

          <div style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '12px', marginBottom: 16, fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ color: '#6B7280' }}>Pasien</span>
              <strong style={{ color: '#111827' }}>{item.pendaftaran?.pasien?.nama ?? '-'}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6B7280' }}>Total Tagihan</span>
              <strong style={{ color: '#059669' }}>{fmtRupiah(item.total ?? 0)}</strong>
            </div>
          </div>

          {error && <p style={{ color: '#DC2626', fontSize: 13, margin: '0 0 14px' }}>{error}</p>}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={btnSecondaryStyle}>Batal</button>
            <button type="button" onClick={handleKonfirmasi} disabled={loading} style={btnSuccessStyle}>
              {loading ? 'Memproses…' : '✓ Ya, Konfirmasi Lunas'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── MODAL TOLAK / BATAL ──────────────────────────────────── */
function ModalTolak({ item, onClose, onSuccess }) {
  const [alasan, setAlasan] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTolak = async () => {
    setLoading(true);
    setError('');
    try {
      await axiosInstance.post(`/admin/transaksi/${item.id}/batal`, { alasan });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message ?? 'Gagal membatalkan transaksi.');
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#ffffff', borderRadius: 16, width: '100%', maxWidth: 400, overflow: 'hidden' }}>
        <div style={{ height: 4, background: 'linear-gradient(90deg, #EF4444, #F87171)' }} />
        <div style={{ padding: '24px' }}>
          <h3 style={{ fontSize: 17, fontWeight: 800, color: '#111827', margin: '0 0 8px' }}>Batalkan Transaksi?</h3>
          <p style={{ fontSize: 13, color: '#4B5563', margin: '0 0 16px', lineHeight: 1.5 }}>
            Transaksi invoice <strong style={{ color: '#111827' }}>{item.nomor_invoice}</strong> akan dibatalkan.
          </p>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Alasan Pembatalan (Opsional)</label>
            <textarea
              style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }}
              placeholder="Contoh: Pasien membatalkan perawatan / kesalahan input..."
              value={alasan}
              onChange={(e) => setAlasan(e.target.value)}
            />
          </div>

          {error && <p style={{ color: '#DC2626', fontSize: 13, margin: '0 0 14px' }}>{error}</p>}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={btnSecondaryStyle}>Batal</button>
            <button type="button" onClick={handleTolak} disabled={loading} style={btnDangerStyle}>
              {loading ? 'Memproses…' : '✕ Ya, Batalkan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── MODAL DETAIL TRANSAKSI ───────────────────────────────── */
function ModalDetailTransaksi({ item, onClose }) {
  if (!item) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#ffffff', borderRadius: 16, width: '100%', maxWidth: 500, overflow: 'hidden' }}>
        <div style={{ height: 4, background: 'linear-gradient(90deg, #2563eb, #60a5fa)' }} />
        <div style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', margin: 0 }}>Detail Transaksi</p>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: '#111827', margin: 0 }}>{item.nomor_invoice ?? '-'}</h3>
            </div>
            <StatusPill status={item.status} />
          </div>

          <div style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '12px', marginBottom: 16, fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ color: '#6B7280' }}>Pasien</span>
              <strong style={{ color: '#111827' }}>{item.pendaftaran?.pasien?.nama ?? '-'}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ color: '#6B7280' }}>Dokter</span>
              <strong style={{ color: '#111827' }}>{item.pendaftaran?.dokter?.nama ?? '-'}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6B7280' }}>Tanggal Transaksi</span>
              <strong style={{ color: '#111827' }}>{fmtDate(item.created_at)}</strong>
            </div>
          </div>

          <h4 style={{ fontSize: 13, fontWeight: 700, color: '#111827', margin: '0 0 10px 0' }}>Rincian Tindakan / Item</h4>
          {item.items && item.items.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {item.items.map((it, idx) => {
                const subtotal = (parseFloat(it.harga_satuan) || 0) * (parseInt(it.qty) || 0);
                return (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, borderBottom: '1px border-dashed #F3F4F6', paddingBottom: 6 }}>
                    <span style={{ color: '#374151' }}>{it.nama_snapshot} <span style={{ color: '#9CA3AF' }}>×{it.qty}</span></span>
                    <strong style={{ color: '#111827' }}>{fmtRupiah(subtotal)}</strong>
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic', margin: '0 0 16px 0' }}>Belum ada rincian item.</p>
          )}

          <div style={{ borderTop: '2px solid #F3F4F6', paddingTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <span style={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>Total Tagihan</span>
            <span style={{ fontSize: 18, fontWeight: 800, color: '#059669' }}>{fmtRupiah(item.total)}</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={btnSecondaryStyle}>Tutup</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── HALAMAN UTAMA TRANSAKSI ──────────────────────────────── */
export default function TransaksiAdmin() {
  const [transaksi, setTransaksi]             = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState('');
  const [activeTab, setActiveTab]             = useState('semua');
  const [modalTagihan, setModalTagihan]       = useState(null);
  const [modalKonfirmasi, setModalKonfirmasi] = useState(null);
  const [modalTolak, setModalTolak]           = useState(null);
  const [modalDetail, setModalDetail]         = useState(null);
  const [search, setSearch]                   = useState('');

  /* Deteksi Layar Mobile */
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchData = useCallback(async () => {
    setError('');
    try {
      const res = await axiosInstance.get('/admin/transaksi');
      const raw = res.data?.data?.data ?? res.data?.data ?? res.data ?? [];
      setTransaksi(Array.isArray(raw) ? raw : []);
    } catch (err) {
      setError(err.response?.data?.message ?? 'Gagal memuat data transaksi.');
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchData();
      setLoading(false);
    })();
  }, [fetchData]);

  const countBy = (s) => transaksi.filter((t) => t.status === s).length;

  const TABS = [
    { key: 'semua', label: 'Semua', count: transaksi.length },
    { key: 'pending_billing', label: 'Perlu Input', count: countBy('pending_billing') },
    { key: 'billed', label: 'Tagihan Siap', count: countBy('billed') },
    { key: 'paid', label: 'Lunas', count: countBy('paid') },
    { key: 'cancelled', label: 'Dibatalkan', count: countBy('cancelled') },
  ];

  const filtered = transaksi.filter((t) => {
    const matchTab = activeTab === 'semua' ? true : t.status === activeTab;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      (t.nomor_invoice ?? '').toLowerCase().includes(q) ||
      (t.pendaftaran?.pasien?.nama ?? '').toLowerCase().includes(q);
    return matchTab && matchSearch;
  });

  const renderActionButtons = (t) => {
    if (t.status === 'pending_billing') {
      return (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => setModalTagihan(t)} style={btnPrimaryStyle}>
            ✏️ Input Tagihan
          </button>
          <button type="button" onClick={() => setModalTolak(t)} style={btnSecondaryStyle}>
            ✕ Batal
          </button>
        </div>
      );
    }
    if (t.status === 'billed') {
      return (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => setModalKonfirmasi(t)} style={btnSuccessStyle}>
            ✓ Tandai Lunas
          </button>
          <button type="button" onClick={() => setModalDetail(t)} style={btnSecondaryStyle}>
            👁️ Detail
          </button>
          <button type="button" onClick={() => setModalTolak(t)} style={btnSecondaryStyle}>
            ✕ Batal
          </button>
        </div>
      );
    }
    return (
      <button type="button" onClick={() => setModalDetail(t)} style={btnSecondaryStyle}>
        👁️ Detail
      </button>
    );
  };

  return (
    <AdminLayout title="">
      <div style={{ padding: isMobile ? '0px' : '4px' }}>
        
        {/* HEADER HALAMAN */}
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: '800', color: '#111827', margin: 0 }}>
            Manajemen Transaksi
          </h1>
          <p style={{ color: '#6B7280', margin: '4px 0 0', fontSize: '13px' }}>
            Input rincian tagihan medis dan konfirmasi pelunasan pembayaran pasien
          </p>
        </div>

        {/* KARTU RINGKASAN */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: isMobile ? '10px' : '16px',
          marginBottom: '20px',
        }}>
          <div style={statCardStyle}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280' }}>Perlu Input Tagihan</div>
            <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '800', color: '#0369a1', marginTop: '4px' }}>
              {countBy('pending_billing')}
            </div>
          </div>
          <div style={statCardStyle}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280' }}>Tagihan Siap</div>
            <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '800', color: '#b45309', marginTop: '4px' }}>
              {countBy('billed')}
            </div>
          </div>
          <div style={statCardStyle}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280' }}>Sudah Lunas</div>
            <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '800', color: '#166534', marginTop: '4px' }}>
              {countBy('paid')}
            </div>
          </div>
          <div style={statCardStyle}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280' }}>Dibatalkan</div>
            <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '800', color: '#991b1b', marginTop: '4px' }}>
              {countBy('cancelled')}
            </div>
          </div>
        </div>

        {error && (
          <div style={{ backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: 8, padding: '12px', fontSize: 13, marginBottom: 20 }}>
            {error}
          </div>
        )}

        {/* CONTEN TABEL / LIST */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}>
          {/* TAB BAR & FILTER SEARCH */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'center',
            borderBottom: '1px solid #E5E7EB',
            padding: isMobile ? '12px' : '0 16px',
            gap: '12px',
          }}>
            <div style={{ display: 'flex', overflowX: 'auto', gap: 4 }}>
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    padding: '12px 14px',
                    border: 'none',
                    background: 'none',
                    fontSize: '13px',
                    fontWeight: activeTab === tab.key ? '700' : '500',
                    color: activeTab === tab.key ? '#2563EB' : '#6B7280',
                    borderBottom: activeTab === tab.key ? '2px solid #2563EB' : '2px solid transparent',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {tab.label}
                  <span style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    backgroundColor: activeTab === tab.key ? '#EFF6FF' : '#F3F4F6',
                    color: activeTab === tab.key ? '#1D4ED8' : '#6B7280',
                  }}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            <div style={{ padding: isMobile ? '0' : '10px 0' }}>
              <input
                type="search"
                placeholder="Cari invoice / nama pasien..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ ...inputStyle, width: isMobile ? '100%' : '240px' }}
              />
            </div>
          </div>

          {/* TABEL DATA */}
          {loading ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: '#6B7280', fontSize: '13px' }}>
              ⏳ Memuat data transaksi…
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '40px 16px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>
              Tidak ada data transaksi yang sesuai filter.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                    <th style={thStyle}>No. Invoice</th>
                    <th style={thStyle}>Pasien</th>
                    <th style={thStyle}>Tanggal</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Total Tagihan</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t, idx) => (
                    <tr key={t.id ?? idx} style={{ borderBottom: idx !== filtered.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                      <td style={tdStyle}>
                        <span style={{ fontFamily: 'monospace', fontWeight: '700', color: '#2563EB' }}>
                          {t.nomor_invoice ?? '-'}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontWeight: '600', color: '#111827' }}>
                        {t.pendaftaran?.pasien?.nama ?? '-'}
                      </td>
                      <td style={tdStyle}>{fmtDate(t.created_at)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <strong style={{ color: t.total > 0 ? '#111827' : '#D97706' }}>
                          {t.total > 0 ? fmtRupiah(t.total) : 'Belum Input'}
                        </strong>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <StatusPill status={t.status} />
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        {renderActionButtons(t)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      {/* MODAL DIALoG */}
      {modalTagihan && (
        <ModalInputTagihan
          item={modalTagihan}
          onClose={() => setModalTagihan(null)}
          onSuccess={() => { setModalTagihan(null); fetchData(); }}
        />
      )}
      {modalKonfirmasi && (
        <ModalKonfirmasiLunas
          item={modalKonfirmasi}
          onClose={() => setModalKonfirmasi(null)}
          onSuccess={() => { setModalKonfirmasi(null); fetchData(); }}
        />
      )}
      {modalTolak && (
        <ModalTolak
          item={modalTolak}
          onClose={() => setModalTolak(null)}
          onSuccess={() => { setModalTolak(null); fetchData(); }}
        />
      )}
      {modalDetail && (
        <ModalDetailTransaksi
          item={modalDetail}
          onClose={() => setModalDetail(null)}
        />
      )}
    </AdminLayout>
  );
}

/* ─── STYLES CONSTANTS ────────────────────────────────────── */
const statCardStyle = {
  backgroundColor: '#FFFFFF',
  borderRadius: '12px',
  border: '1px solid #E5E7EB',
  padding: '16px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

const labelStyle = {
  display: 'block',
  fontSize: '12px',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '6px',
};

const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: '8px',
  border: '1px solid #D1D5DB',
  fontSize: '13px',
  outline: 'none',
  boxSizing: 'border-box',
  backgroundColor: '#FFFFFF',
};

const thStyle = {
  padding: '12px 14px',
  color: '#4B5563',
  fontWeight: '600',
  fontSize: '12px',
};

const tdStyle = {
  padding: '12px 14px',
  color: '#374151',
};

const btnPrimaryStyle = {
  padding: '6px 12px',
  backgroundColor: '#2563EB',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '6px',
  fontSize: '12px',
  fontWeight: '600',
  cursor: 'pointer',
};

const btnSuccessStyle = {
  padding: '6px 12px',
  backgroundColor: '#059669',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '6px',
  fontSize: '12px',
  fontWeight: '600',
  cursor: 'pointer',
};

const btnDangerStyle = {
  padding: '6px 12px',
  backgroundColor: '#DC2626',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '6px',
  fontSize: '12px',
  fontWeight: '600',
  cursor: 'pointer',
};

const btnSecondaryStyle = {
  padding: '6px 12px',
  backgroundColor: '#FFFFFF',
  color: '#374151',
  border: '1px solid #D1D5DB',
  borderRadius: '6px',
  fontSize: '12px',
  fontWeight: '600',
  cursor: 'pointer',
};